import Icon from '../../../components/AppIcon';
import { NotificationItem } from '../types';

interface NotificationPanelProps {
  notifications: NotificationItem[];
  onActionClick?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

const NotificationPanel = ({ notifications, onActionClick, onDismiss }: NotificationPanelProps) => {
  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'confirmation':
        return { name: 'CheckCircle', color: 'var(--color-warning)' };
      case 'verification':
        return { name: 'AlertCircle', color: 'var(--color-primary)' };
      case 'reminder':
        return { name: 'Bell', color: 'var(--color-accent)' };
      default:
        return { name: 'Info', color: 'var(--color-muted-foreground)' };
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const actionRequiredNotifications = notifications.filter(n => n.actionRequired);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="p-4 sm:p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
            <span className="text-xl">🔔</span>
            <span>Notifications</span>
          </h3>
          {actionRequiredNotifications.length > 0 && (
            <span className="px-2 py-1 bg-warning text-warning-foreground text-xs font-bold rounded-full shadow-sm animate-pulse">
              {actionRequiredNotifications.length} Action Required
            </span>
          )}
        </div>
      </div>

      <div className="divide-y divide-border max-h-96 overflow-y-auto scrollbar-thin">
        {notifications.map((notification) => {
          const icon = getNotificationIcon(notification.type);

          return (
            <div
              key={notification.id}
              className="p-3 sm:p-4 hover:bg-muted/50 transition-all duration-200 cursor-default"
            >
              <div className="flex gap-2 sm:gap-3">
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform hover:scale-110"
                  style={{ backgroundColor: `${icon.color}15` }}
                >
                  <Icon name={icon.name} size={18} color={icon.color} className="sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-foreground mb-1 leading-relaxed">{notification.message}</p>
                  <span className="text-xs text-muted-foreground font-medium">
                    {formatTimestamp(notification.timestamp)}
                  </span>
                  {notification.actionRequired && (
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                      <button
                        onClick={() => onActionClick?.(notification.id)}
                        className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-primary/90 hover:shadow-md transition-all duration-200 transform hover:scale-105"
                      >
                        Take Action
                      </button>
                      <button
                        onClick={() => onDismiss?.(notification.id)}
                        className="px-3 py-1.5 border border-border text-foreground text-xs font-semibold rounded-lg hover:bg-muted hover:border-muted-foreground transition-all duration-200"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationPanel;